import React, { useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
} from '@mui/material';
import { motion } from 'framer-motion';
import HomeLayout from '../../asset/layout/HomeLayout';
import ExperiencesTab from './ExperiencesTab';
import ExperienceOrdersTab from './ExperienceOrdersTab';

import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';

const fadeUp = (i = 0) => ({
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
});

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: '100%' }}>
    <Box sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            background: gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: '0.7rem',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.75rem',
              color: '#0f172a',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}
          >
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const ExperiencesManagement = ({ defaultTab = 0 }) => {
  const [currentTab, setCurrentTab] = useState(defaultTab);

  // Stats state from child components
  const [experienceStats, setExperienceStats] = useState({
    totalExperiences: '—',
    activeExperiences: '—',
    totalSlots: '—',
  });

  const [orderStats, setOrderStats] = useState({
    totalOrders: '—',
    pendingApproval: '—',
    underVerification: '—',
    needPayment: '—',
    confirmedOrders: '—',
  });

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: '100%',
          background: 'linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)',
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        {/* Hero Section */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp()}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: '24px',
              background: 'linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(17, 64, 155, 0.35)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -30,
                width: 220,
                height: 220,
                borderRadius: '50%',
                bgcolor: alpha('#fff', 0.06),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <ExploreOutlinedIcon />
              <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.1em', opacity: 0.85 }}>
                EXPERIENCES MODULE
              </Typography>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
              Experiences & Bookings Management
            </Typography>
            <Typography sx={{ mt: 1, opacity: 0.85, maxWidth: 640 }}>
              Curate travel, adventure, wellness, and lifestyle experiences. Oversee public catalog browsing,
              manage capacity & pricing, and track the full booking lifecycle state machine.
            </Typography>
          </Box>
        </motion.div>

        {/* Dynamic Metric StatCards */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp(1)}>
          {currentTab === 0 ? (
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={ExploreOutlinedIcon}
                  label="Total Experiences"
                  value={experienceStats.totalExperiences}
                  accent="#11409B"
                  gradient="linear-gradient(135deg, #0c3483, #11409B)"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={CheckCircleOutlineIcon}
                  label="Active Experiences"
                  value={experienceStats.activeExperiences}
                  accent="#16a34a"
                  gradient="linear-gradient(135deg, #15803d, #16a34a)"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={PeopleAltOutlinedIcon}
                  label="Total Slots Capacity"
                  value={experienceStats.totalSlots}
                  accent="#7c3aed"
                  gradient="linear-gradient(135deg, #6d28d9, #7c3aed)"
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  icon={ConfirmationNumberOutlinedIcon}
                  label="Total Orders"
                  value={orderStats.totalOrders}
                  accent="#11409B"
                  gradient="linear-gradient(135deg, #0c3483, #11409B)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  icon={HourglassEmptyIcon}
                  label="Pending Approval"
                  value={orderStats.pendingApproval}
                  accent="#d97706"
                  gradient="linear-gradient(135deg, #b45309, #d97706)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  icon={VerifiedOutlinedIcon}
                  label="In Verification"
                  value={orderStats.underVerification}
                  accent="#2563eb"
                  gradient="linear-gradient(135deg, #1d4ed8, #2563eb)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  icon={PaymentOutlinedIcon}
                  label="Need To Payment"
                  value={orderStats.needPayment}
                  accent="#7c3aed"
                  gradient="linear-gradient(135deg, #6d28d9, #7c3aed)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  icon={TaskAltOutlinedIcon}
                  label="Confirmed Orders"
                  value={orderStats.confirmedOrders}
                  accent="#059669"
                  gradient="linear-gradient(135deg, #047857, #059669)"
                />
              </Grid>
            </Grid>
          )}
        </motion.div>

        {/* Tab Navigation */}
        <PanelCard sx={{ mb: 3, px: 2, pt: 1 }}>
          <Tabs
            value={currentTab}
            onChange={(_, val) => setCurrentTab(val)}
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
              },
            }}
          >
            <Tab
              icon={<ExploreOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label="Experiences Catalog & Management"
            />
            <Tab
              icon={<ConfirmationNumberOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label="Booking Orders & Lifecycle Tracking"
            />
          </Tabs>
        </PanelCard>

        {/* Tab Content */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp(2)}>
          {currentTab === 0 ? (
            <ExperiencesTab onStatsUpdate={setExperienceStats} />
          ) : (
            <ExperienceOrdersTab onStatsUpdate={setOrderStats} />
          )}
        </motion.div>
      </Box>
    </HomeLayout>
  );
};

export default ExperiencesManagement;
